// Haversine distance in metres between two lat/long points
export function distanceMetres(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export const GPS_RADIUS_METRES = Number(
  process.env.REACT_APP_GPS_RADIUS_METERS || 50
);

// A settled position reading. accuracy is the phone's own estimate in metres.
export interface GpsFix {
  latitude: number;
  longitude: number;
  accuracy: number;
}

// How long we are willing to wait for the phone to sharpen its fix, and the
// accuracy at which we stop waiting early. Phones, iPhones especially, often
// hand over a rough first reading and improve over the next few seconds, so
// we keep listening and take the best reading rather than the first one.
const MAX_WAIT_MS = 12000;
const GOOD_ENOUGH_METRES = 20;

export function getPosition(): Promise<GpsFix> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('GPS not available on this device'));
      return;
    }

    let best: GeolocationPosition | null = null;
    let watchId: number | null = null;
    let timer: number | undefined;
    let done = false;

    const cleanup = () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (timer !== undefined) window.clearTimeout(timer);
    };

    const finish = () => {
      if (done) return;
      done = true;
      cleanup();
      if (best) {
        resolve({
          latitude: best.coords.latitude,
          longitude: best.coords.longitude,
          accuracy: best.coords.accuracy,
        });
      } else {
        reject(
          new Error(
            'Could not get a GPS fix. Check location permissions, step outside, and try again.'
          )
        );
      }
    };

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos;
        if (pos.coords.accuracy <= GOOD_ENOUGH_METRES) finish();
      },
      (err) => {
        if (best) {
          finish();
          return;
        }
        done = true;
        cleanup();
        reject(
          new Error(
            err.code === 1
              ? 'Location permission is off for this site. Allow location access and try again.'
              : 'Could not read GPS. Step outside with a clear view of the sky and try again.'
          )
        );
      },
      {
        enableHighAccuracy: true,
        timeout: MAX_WAIT_MS,
        maximumAge: 0,
      }
    );

    timer = window.setTimeout(finish, MAX_WAIT_MS);
  });
}
